import React, { useState } from 'react';
import { widgetKeyHeaders } from '../config/api';

/**
 * FeedbackRating Component
 *
 * Premium 3-tier feedback system for AI responses
 * Design: "Helpful" / "Somewhat helpful" / "Wrong answer" pattern
 * 
 * Improved UX:
 * - Clearer labels for better analytics
 * - Text input only for wrong answers (reduces friction)
 * - Professional tone for B2B2C platform
 *
 * @param {Object} props
 * @param {string} props.messageId - Unique ID for the message being rated
 * @param {string} props.questionText - Original question text
 * @param {string} props.answerText - AI response text
 * @param {function} props.onFeedback - Callback when feedback is submitted
 * @param {string} props.companySlug - Company identifier for analytics
 */
const FeedbackRating = ({
  messageId,
  questionText,
  answerText,
  onFeedback,
  companySlug
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFeedback = async (type) => {
    setFeedbackType(type);
    setFeedbackGiven(true);

    // For "wrong_answer", show text input for detailed feedback
    if (type === 'wrong_answer') {
      setShowTextInput(true);
      return; // Wait for user to submit text feedback
    }

    // For "helpful" and "somewhat_helpful", submit immediately
    await submitFeedback(type, '');
  };

  const submitFeedback = async (type, text) => {
    setSubmitting(true);

    // Calculate satisfaction score for analytics
    const satisfactionScore = {
      'helpful': 1.0,
      'somewhat_helpful': 0.5,
      'wrong_answer': 0.0
    }[type] || 0.0;

    const feedbackData = {
      message_id: messageId,
      feedback_type: type,
      question: questionText,
      answer: answerText,
      feedback_text: text,
      company_slug: companySlug,
      satisfaction_score: satisfactionScore,
      timestamp: new Date().toISOString(),
    };

    try {
      // Call API to log feedback
      if (onFeedback) {
        await onFeedback(feedbackData);
      }

      // Also send to backend endpoint
      const baseUrl = window.API_BASE || 'http://localhost:8000';
      await fetch(`${baseUrl}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...widgetKeyHeaders() },
        body: JSON.stringify(feedbackData),
      });

      // Feedback submitted successfully
    } catch (error) {
      console.error('[Feedback] Error submitting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTextSubmit = async () => {
    await submitFeedback(feedbackType, feedbackText);
    setShowTextInput(false);
  };

  // If feedback already given, show confirmation
  if (feedbackGiven && !showTextInput) {
    return (
      <div className="feedback-rating-confirmed">
        {feedbackType === 'helpful' && (
          <div className="feedback-message success">
            <span className="feedback-icon">✅</span>
            <span className="feedback-text">Thanks! We're glad this helped.</span>
          </div>
        )}
        {feedbackType === 'somewhat_helpful' && (
          <div className="feedback-message info">
            <span className="feedback-icon">💡</span>
            <span className="feedback-text">Thanks for the feedback. We're working to improve!</span>
          </div>
        )}
        {feedbackType === 'wrong_answer' && (
          <div className="feedback-message error">
            <span className="feedback-icon">📝</span>
            <span className="feedback-text">Thanks for letting us know. We'll review this and improve.</span>
          </div>
        )}
      </div>
    );
  }

  // Text feedback input for wrong answers
  if (showTextInput) {
    return (
      <div className="feedback-text-input">
        <p className="feedback-prompt">What was wrong with the answer?</p>
        <textarea
          className="feedback-textarea"
          placeholder="Tell us what was incorrect or what you expected instead..."
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={3}
          autoFocus
        />
        <div className="feedback-actions">
          <button
            className="feedback-btn feedback-submit"
            onClick={handleTextSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
          <button
            className="feedback-btn feedback-skip"
            onClick={() => {
              submitFeedback(feedbackType, '');
              setShowTextInput(false);
            }}
            disabled={submitting}
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  // Main feedback buttons - Simplified 3-option design optimized for mobile
  return (
    <div className="feedback-rating feedback-rating-compact">
      <div className="feedback-buttons">
        <button
          className="feedback-btn feedback-helpful"
          onClick={() => handleFeedback('helpful')}
          title="Helpful"
          aria-label="Helpful - this answer was good"
        >
          <span className="feedback-icon">👍</span>
          <span className="feedback-label">Good</span>
        </button>
        <button
          className="feedback-btn feedback-somewhat"
          onClick={() => handleFeedback('somewhat_helpful')}
          title="Needs improvement"
          aria-label="Needs improvement - this answer was okay but could be better"
        >
          <span className="feedback-icon">👎</span>
          <span className="feedback-label">Bad</span>
        </button>
        <button
          className="feedback-btn feedback-wrong"
          onClick={() => handleFeedback('wrong_answer')}
          title="Report issue"
          aria-label="Report issue - this answer was incorrect"
        >
          <span className="feedback-icon">🚩</span>
          <span className="feedback-label">Report</span>
        </button>
      </div>
    </div>
  );
};

export default FeedbackRating;
